'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type { ReactNode } from 'react'
import { useId } from 'react'
import { useHover } from 'react-aria'
import type { PrivateUiInputProps, PublicInputProps } from '../../_shared/AbstractInput.client'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import { Checkbox } from './Checkbox.client'

type UICheckboxOwnProps = {
  label: ReactNode
  additionalText?: string
  defaultWidth?: boolean
  className?: string
  showHoverUnderline?: boolean
}

type UICheckboxProps = PrivateUiInputProps<boolean, UICheckboxOwnProps>

export function UICheckbox({
  label,
  additionalText,
  name,
  error,
  value,
  isRequired,
  isDisabled,
  isLoading,
  onChange,
  defaultWidth = false,
  className,
  showHoverUnderline = false,
}: UICheckboxProps) {
  const inputId = useId()
  const { isHovered, hoverProps } = useHover({})

  const hasError = Boolean(error?.message)
  const shouldDisplayError = !isLoading && !isDisabled && hasError
  const errorId = shouldDisplayError ? `${inputId}-error` : false

  const labelContent = (
    <span {...hoverProps} className={twMerge(showHoverUnderline && isHovered && 'underline')}>
      {label}
      {isRequired ? ' *' : null}
    </span>
  )

  return (
    <div className={defaultWidth ? 'w-auto' : 'w-full'} data-testid="ui-checkbox">
      <Checkbox
        id={inputId}
        name={name}
        isSelected={value}
        onChange={onChange}
        isDisabled={isDisabled}
        isInvalid={shouldDisplayError}
        isRequired={isRequired}
        errorId={errorId}
        shouldDisplayError={shouldDisplayError}
        errorMessage={error?.message}
        additionalText={additionalText}
        className={className}
        data-testid="ui-checkbox/input"
      >
        {labelContent}
      </Checkbox>
    </div>
  )
}

type CheckboxInputProps = PublicInputProps<object, 'required'> & {
  additionalText?: string
  defaultWidth?: boolean
  className?: string
}

/**
 * @example
 * ```
 * <CheckboxInput
 *   name="terms"
 *   label="I agree to the terms and conditions"
 *   required
 *   asyncValidate={checkNeedsTsAndCs}
 * />
 * ```
 */
export function CheckboxInput({
  name,
  label,
  additionalText,
  defaultWidth,
  className,
  ...rules
}: CheckboxInputProps) {
  return (
    <AbstractInput
      {...rules}
      name={name}
      render={({ field, fieldState, formState }) => (
        <UICheckbox
          {...field}
          name={name}
          label={label}
          additionalText={additionalText}
          error={fieldState.error}
          isLoading={false}
          isDisabled={field.disabled}
          isRequired={fieldState.isRequired}
          isSubmitting={formState.isSubmitting}
          defaultWidth={defaultWidth}
          className={className}
        />
      )}
    />
  )
}
